import React from "react";
import { Link } from "react-router-dom";

import { faArrowLeft } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { dispatchAnalyticsTrack } from "~/lib/segment";

import { AuthBase } from "../AuthBase";
import { Button, Input, SignupHint } from "../components/";
import { WhitelabelPane } from "../panes";
import type { AuthProps } from "../types";

import styles from "../css/Auth.module.css";

interface FindBySubdomainState {
	subdomainInput: string;
	searchFocused: boolean;
	errorMessage: string | undefined;
	loading: boolean;
}

type FindBySubdomainResult =
	| {
			success: true;
			result: string;
	  }
	| {
			success: false;
	  };

export default class FindBySubdomain extends AuthBase<AuthProps, FindBySubdomainState> {
	state: FindBySubdomainState = {
		subdomainInput: "",
		searchFocused: false,
		loading: false,
		errorMessage: undefined,
	};

	documentTitle = "Find | Login";

	getPaneComponent() {
		return WhitelabelPane;
	}

	getPaneProps() {
		return { bgStyle: "purple" as const };
	}

	findWorkspace = () => {
		const { subdomainInput } = this.state;

		if (subdomainInput) {
			this.setState({ loading: true });

			const analyticsData = { feature: "Authentication", subdomain: subdomainInput };
			const redirectBackToDesktopOnLastOauthStep = window.location.search.includes(
				"redirectBackToDesktopOnLastOauthStep=1",
			);
			const data = new FormData();

			data.append("subdomain", subdomainInput);

			fetch("/auth/search/subdomain/", {
				method: "POST",
				body: data,
				headers: {
					"x-client-session-id": window.CLIENT_SESSION_ID,
					"x-csrftoken": window.CSRF_TOKEN,
					"x-requested-with": "XMLHttpRequest",
				},
			})
				.then((res: Response) => {
					return res.json();
				})
				.then((data: FindBySubdomainResult) => {
					this.setState({ loading: false });

					if (data.success) {
						dispatchAnalyticsTrack("Find By Subdomain Step Completed", analyticsData);

						// FindBySubdomain is used for login purposes. If the user should be redirected back to the desktop app
						// after the last OAuth step, we need to append a query parameter to the URL. And we need to use the
						// /auth/login/ url since / might redirect the user to /auth/login/ if the user is not logged in
						// and at that point the redirectBackToDesktopOnLastOauthStep parameter will be part of the "next" url.
						window.location.href =
							data.result +
							(redirectBackToDesktopOnLastOauthStep ? "/auth/login/?redirectBackToDesktopOnLastOauthStep=1" : "");
					} else {
						dispatchAnalyticsTrack("Find By Subdomain Step Failed", {
							reason: "subdomain_not_found",
							...analyticsData,
						});
						this.setState({ errorMessage: "We didn't find a Workspace at that URL" });
					}
				})
				.catch(() => {
					dispatchAnalyticsTrack("Find By Subdomain Step Failed", analyticsData);
					this.setState({ loading: false });
				});
		}
	};

	onSearchValueChange = (e: React.SyntheticEvent<HTMLInputElement, Event>) => {
		this.setState({ subdomainInput: e.currentTarget.value, errorMessage: "" });
	};

	onSearchKeydown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") {
			e.preventDefault();
			this.findWorkspace();
		}
	};

	onSearchFocus = () => this.setState({ searchFocused: true });

	onSearchBlur = () => this.setState({ searchFocused: false });

	clearSearchValue = () => this.setState({ subdomainInput: "" });

	renderStep() {
		const rootDomain = window.ROOT_DOMAIN ?? ".fellow.app";
		const { subdomainInput, searchFocused, loading, errorMessage } = this.state;
		const { search } = this.props.history.location;

		const ssoLocation = {
			pathname: "/auth/login/",
			search,
		};

		const findEmailLocation = {
			pathname: "/auth/find/email/",
			search,
		};

		return (
			<div className={styles.authFlex}>
				<div className={styles.authFind}>
					<h2>Enter your workspace URL</h2>

					<div className={styles.authFindBody}>
						<div
							data-test-id="find-workspace-url"
							className={cx(styles.inputWithLabel, { [styles.focused]: searchFocused, [styles.error]: errorMessage })}
						>
							<Input
								value={subdomainInput}
								placeholder="myworkspace"
								onChange={this.onSearchValueChange}
								onKeyDown={this.onSearchKeydown}
								onClear={this.clearSearchValue}
								onFocus={this.onSearchFocus}
								onBlur={this.onSearchBlur}
								className={styles.authInput}
							/>
							<div className={styles.inputLabel}>{rootDomain}</div>
						</div>
						<br />
						<h5>
							<span>Not sure what your workspace URL is?</span> <Link to={findEmailLocation}>Find it here</Link>
						</h5>
					</div>

					<Button primary onClick={this.findWorkspace} disabled={!subdomainInput || loading}>
						Continue
					</Button>

					<h5 className={styles.authFindBody}>
						<Link to={ssoLocation}>Log in with Single sign-on</Link>
					</h5>
				</div>
				{this.props.history.length > 1 && (
					<div className={styles.backButton} onClick={this.onBackClick}>
						<FontAwesomeIcon icon={faArrowLeft} />
						<span>Back</span>
					</div>
				)}
			</div>
		);
	}

	renderFooter() {
		return <SignupHint />;
	}
}
